import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Layers3, Plus, Loader2, Play, Trash2 } from "lucide-react";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

function fmtRange(min, max, currency) {
  if (min == null && max == null) return "—";
  const f = (n) => {
    try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || "GBP", maximumFractionDigits: 0 }).format(n); }
    catch { return `${currency || ""} ${n}`; }
  };
  if (min != null && max != null) return `${f(min)} – ${f(max)}`;
  return f(min ?? max);
}

function ProgrammeForm({ initial, onSubmit, onCancel, submitting }) {
  const [draft, setDraft] = useState({
    name: initial?.name || "",
    summary_md: initial?.summary_md || "",
    default_duration_weeks: initial?.default_duration_weeks ?? "",
    default_cohort_size: initial?.default_cohort_size ?? "",
    default_modules: (initial?.default_modules || []).join("\n"),
    default_value_min: initial?.default_value_min ?? "",
    default_value_max: initial?.default_value_max ?? "",
    default_currency: initial?.default_currency || "GBP",
    region: initial?.region || "",
  });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs text-slate-600">Name *</Label>
        <Input data-testid="programme-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g., Senior Leader Development — 12 weeks" />
      </div>
      <div>
        <Label className="text-xs text-slate-600">Summary</Label>
        <Textarea data-testid="programme-summary-input" rows={3} value={draft.summary_md} onChange={(e) => setDraft({ ...draft, summary_md: e.target.value })} placeholder="What is this programme, who it's for, what they walk away with..." />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-600">Default duration (weeks)</Label>
          <Input type="number" value={draft.default_duration_weeks} onChange={(e) => setDraft({ ...draft, default_duration_weeks: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Default cohort size</Label>
          <Input type="number" value={draft.default_cohort_size} onChange={(e) => setDraft({ ...draft, default_cohort_size: e.target.value })} />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-600">Modules (one per line)</Label>
        <Textarea
          data-testid="programme-modules-input"
          rows={4}
          value={draft.default_modules}
          onChange={(e) => setDraft({ ...draft, default_modules: e.target.value })}
          placeholder={"Strategy\nLeading Self\nLeading Others\nLeading the Organisation"}
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-slate-600">Value min</Label>
          <Input type="number" value={draft.default_value_min} onChange={(e) => setDraft({ ...draft, default_value_min: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Value max</Label>
          <Input type="number" value={draft.default_value_max} onChange={(e) => setDraft({ ...draft, default_value_max: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Currency</Label>
          <select value={draft.default_currency} onChange={(e) => setDraft({ ...draft, default_currency: e.target.value })} className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm">
            {["GBP", "USD", "NGN", "EUR"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-600">Region</Label>
        <select value={draft.region} onChange={(e) => setDraft({ ...draft, region: e.target.value })} className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm">
          <option value="">—</option>
          <option value="UK">UK</option>
          <option value="NG">NG</option>
          <option value="RoA">RoA</option>
        </select>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button
          data-testid="programme-submit-btn"
          disabled={submitting}
          onClick={() => {
            if (!draft.name?.trim()) return toast.error("Name required");
            const body = { ...draft };
            body.default_duration_weeks = body.default_duration_weeks ? parseInt(body.default_duration_weeks) : null;
            body.default_cohort_size = body.default_cohort_size ? parseInt(body.default_cohort_size) : null;
            body.default_value_min = body.default_value_min ? parseFloat(body.default_value_min) : null;
            body.default_value_max = body.default_value_max ? parseFloat(body.default_value_max) : null;
            body.default_modules = (body.default_modules || "").split("\n").map((s) => s.trim()).filter(Boolean);
            if (!body.region) body.region = null;
            onSubmit(body);
          }}
        >
          {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save
        </Button>
      </DialogFooter>
    </div>
  );
}

function UseProgrammeDialog({ open, onClose, programme, workspaceId, onCreated }) {
  const [draft, setDraft] = useState({ client_name: "", contracted_value: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setDraft({ client_name: "", contracted_value: "" });
  }, [open]);

  const submit = async () => {
    if (!draft.client_name?.trim()) return toast.error("Client name required");
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/programmes/${programme.programme_id}/use`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_name: draft.client_name.trim(),
          contracted_value: draft.contracted_value ? parseFloat(draft.contracted_value) : null,
        }),
      });
      if (!r.ok) throw new Error("Failed");
      const d = await r.json();
      toast.success("Engagement created from programme");
      onCreated?.(d);
      onClose?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!programme) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="use-programme-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5 text-blue-600" /> Use programme
          </DialogTitle>
          <DialogDescription>
            Spawn a new engagement seeded from <strong>{programme.name}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-600">Client</Label>
            <Input data-testid="use-client-input" value={draft.client_name} onChange={(e) => setDraft({ ...draft, client_name: e.target.value })} placeholder="Client name" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Contracted value ({programme.default_currency})</Label>
            <Input data-testid="use-value-input" type="number" value={draft.contracted_value} onChange={(e) => setDraft({ ...draft, contracted_value: e.target.value })} placeholder={fmtRange(programme.default_value_min, programme.default_value_max, programme.default_currency)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="use-submit-btn">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create engagement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProgrammesTab({ workspaceId, onEngagementCreated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // programme being edited or 'new'
  const [submitting, setSubmitting] = useState(false);
  const [useProg, setUseProg] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/programmes`, { credentials: "include" });
      if (r.ok) setItems(await r.json());
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const submitProgramme = async (body) => {
    setSubmitting(true);
    try {
      const url = editing && editing !== "new"
        ? `${API}/v2/workspaces/${workspaceId}/programmes/${editing.programme_id}`
        : `${API}/v2/workspaces/${workspaceId}/programmes`;
      const method = editing && editing !== "new" ? "PATCH" : "POST";
      const r = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`Failed (${r.status})`);
      toast.success(editing && editing !== "new" ? "Programme updated" : "Programme created");
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (p) => {
    if (!confirm(`Soft-delete "${p.name}"?`)) return;
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/programmes/${p.programme_id}`, {
        method: "DELETE", credentials: "include",
      });
      if (!r.ok) throw new Error("Delete failed");
      toast.success("Programme deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="programmes-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Reusable programme blueprints. Use them to spin up new engagements quickly.</p>
        <Button onClick={() => { setCreating(true); setEditing("new"); }} data-testid="programme-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New programme
        </Button>
      </div>

      {loading ? (
        <Card className="border-slate-200"><CardContent className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</CardContent></Card>
      ) : items.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="programmes-empty"><Layers3 className="h-8 w-8 mx-auto text-slate-300 mb-2" />No programme blueprints yet. Capture your first reusable design.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((p) => (
            <Card key={p.programme_id} className="border-slate-200 hover:border-blue-300 transition-colors" data-testid={`programme-card-${p.programme_id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-slate-800 text-sm">{p.name}</h3>
                  {p.region && <Badge variant="outline" className="text-xs">{p.region}</Badge>}
                </div>
                {p.summary_md && <p className="text-xs text-slate-600 line-clamp-3">{p.summary_md}</p>}
                <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                  {p.default_duration_weeks != null && <span>{p.default_duration_weeks} weeks</span>}
                  {p.default_cohort_size != null && <span>· cohort {p.default_cohort_size}</span>}
                  <span>· {fmtRange(p.default_value_min, p.default_value_max, p.default_currency)}</span>
                </div>
                {(p.default_modules || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.default_modules.map((m, i) => (
                      <span key={i} className="text-[10px] bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">{m}</span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <Button size="sm" onClick={() => setUseProg(p)} data-testid={`programme-use-${p.programme_id}`}>
                    <Play className="h-3 w-3 mr-1" /> Use
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`programme-edit-${p.programme_id}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p)} className="text-red-600 hover:text-red-700 ml-auto" data-testid={`programme-delete-${p.programme_id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="programme-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers3 className="h-5 w-5 text-blue-600" />
              {editing === "new" ? "New programme" : "Edit programme"}
            </DialogTitle>
            <DialogDescription>Define the blueprint. You can use this template to spawn engagements pre-filled with scope and commercials.</DialogDescription>
          </DialogHeader>
          {editing && (
            <ProgrammeForm
              initial={editing === "new" ? null : editing}
              submitting={submitting}
              onCancel={() => { setEditing(null); setCreating(false); }}
              onSubmit={submitProgramme}
            />
          )}
        </DialogContent>
      </Dialog>

      <UseProgrammeDialog
        open={!!useProg}
        onClose={() => setUseProg(null)}
        programme={useProg}
        workspaceId={workspaceId}
        onCreated={(d) => onEngagementCreated?.(d)}
      />
    </div>
  );
}
